import { fetchEventSource } from '@microsoft/fetch-event-source';
import type {
    ChatHistoryRequestType,
    ChatHistoryResponse,
    ChatMessageRow,
    ConversationListResponse,
    ConversationSamplesResponse,
    CorrelationStatusResponse,
    DeleteConversationResponse,
    ForkConversationResponse,
    QueryTableArgs,
    QueryTableResponse,
    ResultStats,
    ShareConversationRequestBody,
    ShareConversationResponse,
    StartResponse,
    StreamPacket,
    ExplainContent,
} from '@packages/types';
import { api } from '@ui/app/api';
import { updateTabLabel } from '@ui/features/Chat/slices/tabsSlice';
import { handleCacheUpdateError } from '@ui/utils/errorHandling';
import { logger } from '@ui/utils/logger';
import {
    STREAM_EVENT_NAMES,
    dispatchStreamEvent,
    isBackendTimeoutStage,
    isChatStreamEvent,
    parseSSEMessage,
    type SSEMessageEvent,
    type SSEOpenResponse,
} from '../types';
import { sessionStore } from '../utils';
import { streamErrorHandler } from '../utils/streamErrorHandler';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 100;

export const chatApi = api.injectEndpoints({
    endpoints: builder => ({
        getChat: builder.query<ChatHistoryResponse, { conversationId: string }>({
            query: ({ conversationId }) => ({
                url: `conversation/${conversationId}`, // NestJS backend doesn't use pagination params in URL
                method: 'GET',
            }),
            transformResponse: (response: ChatHistoryResponse) => {
                try {
                    let latestTitle = response.conversation_title || '';
                    if (Array.isArray(response.messages)) {
                        for (const m of response.messages) {
                            const ss =
                                m?.content?.role === 'assistant' &&
                                typeof (m as any)?.content?.content?.summary_substring === 'string'
                                    ? String((m as any).content.content.summary_substring).trim()
                                    : '';
                            if (ss) {
                                latestTitle = ss;
                            }
                        }
                    }
                    return { ...response, conversation_title: latestTitle };
                } catch {
                    return response;
                }
            },
            providesTags: (_res, _err, arg) => [
                { type: 'Conversation', id: arg.conversationId },
                'Messages',
                'Feedback',
            ],
        }),

        getPatientChat: builder.query<ChatHistoryResponse, { conversationId: string; patientId: string }>({
            query: ({ conversationId, patientId }) => ({
                url: `conversation/${conversationId}/patient/${patientId}`, // Matches NestJS route
                method: 'GET',
            }),
            transformResponse: (response: ChatHistoryResponse) => {
                try {
                    let latestTitle = response.conversation_title || '';
                    if (Array.isArray(response.messages)) {
                        for (const m of response.messages) {
                            const ss =
                                m?.content?.role === 'assistant' &&
                                typeof (m as any)?.content?.content?.summary_substring === 'string'
                                    ? String((m as any).content.content.summary_substring).trim()
                                    : '';
                            if (ss) {
                                latestTitle = ss;
                            }
                        }
                    }
                    return { ...response, conversation_title: latestTitle };
                } catch {
                    return response;
                }
            },
            providesTags: (_res, _err, arg) => [
                { type: 'Conversation', id: `${arg.conversationId}:${arg.patientId}` },
                'Messages',
                'Feedback',
            ],
        }),

        getConversationByMessageId: builder.query<
            { conversation_id: string; conversation_title?: string; message: unknown },
            { messageId: string }
        >({
            query: ({ messageId }) => ({
                url: `conversation/by-message/${messageId}`,
                method: 'GET',
            }),
            providesTags: (_res, _err, arg) => [{ type: 'Messages', id: arg.messageId }],
        }),

        explainConversation: builder.mutation<
            ExplainContent,
            {
                conversationId: string;
                messageId?: string;
                patientId?: string;
                page?: number;
                page_size?: number;
                original_query?: string;
                sql_query?: string;
                result_data?: any[];
                query_type?: string;
                sql_explanation?: string | null;
            }
        >({
            query: ({
                conversationId,
                messageId,
                patientId,
                page,
                page_size,
                original_query,
                sql_query,
                result_data,
                query_type,
                sql_explanation,
            }) => ({
                url: `conversation/${conversationId}/explain`,
                method: 'POST',
                body: {
                    ...(messageId ? { message_id: messageId } : {}),
                    ...(patientId ? { patient_id: patientId } : {}),
                    ...(page ? { page } : {}),
                    ...(page_size ? { page_size } : {}),
                    ...(original_query ? { original_query } : {}),
                    ...(sql_query ? { sql_query } : {}),
                    ...(result_data ? { result_data } : {}),
                    ...(query_type ? { query_type } : {}),
                    ...(sql_explanation !== undefined ? { sql_explanation } : {}),
                },
            }),
            invalidatesTags: (_res, _err, arg) => [
                { type: 'Conversation', id: arg.conversationId },
                ...(arg.patientId
                    ? ([{ type: 'Conversation', id: `${arg.conversationId}:${arg.patientId}` }] as const)
                    : []),
                'Messages',
            ],
        }),

        getQueryStats: builder.mutation<
            ResultStats,
            {
                conversationId: string;
                messageId?: string;
                patientId?: string;
            }
        >({
            query: ({ conversationId, messageId, patientId }) => ({
                url: `conversation/${conversationId}/stats`,
                method: 'POST',
                body: {
                    ...(messageId ? { message_id: messageId } : {}),
                    ...(patientId ? { patient_id: patientId } : {}),
                },
            }),
        }),

        getConversationSamples: builder.query<ConversationSamplesResponse, { question?: string; type?: string }>({
            query: ({ question = '', type = 'cohort' }) => ({
                url: `conversation/samples`,
                params: {
                    question_filter: question,
                    conversation_type: type,
                },
                method: 'GET',
            }),
            providesTags: ['Samples'],
        }),

        sendMessage: builder.mutation<
            StartResponse,
            { message: string; conversationId?: string; patientId?: string; path?: string }
        >({
            query: ({ message, conversationId, patientId, path }) => ({
                url: `conversation`, // NestJS POST /conversation
                method: 'POST',
                body: {
                    user_message: message, // Backend expects 'user_message' not 'message'
                    ...(conversationId ? { conversation_id: conversationId } : {}),
                    ...(patientId ? { patient_id: patientId } : {}),
                    ...(path ? { path } : {}),
                },
            }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
                // Optimistically add user message to the UI immediately
                const tempTimestamp = new Date().toISOString();
                const optimisticUserMessage: ChatMessageRow = {
                    BatchId: 0,
                    ConversationId: _arg.conversationId || 'temp',
                    Timestamp: tempTimestamp,
                    UserId: 'local',
                    content: {
                        role: 'user',
                        content: _arg.message,
                    },
                    ...(typeof _arg.patientId === 'string' ? { patientId: _arg.patientId } : {}),
                };

                // Add optimistic update to cache (if conversation exists)
                const patches: Array<{ undo: () => void }> = [];

                if (_arg.conversationId) {
                    try {
                        const patch = dispatch(
                            chatApi.util.updateQueryData('getChat', { conversationId: _arg.conversationId }, draft => {
                                if (!draft) {
                                    return {
                                        messages: [optimisticUserMessage],
                                        conversation_title: '',
                                        conversation_full_path: '',
                                        is_patient_query: !!_arg.patientId,
                                        pinned: false,
                                    } as ChatHistoryResponse;
                                }
                                if (!draft.messages) {
                                    draft.messages = [];
                                }
                                draft.messages.push(optimisticUserMessage);
                                return draft;
                            })
                        );
                        patches.push(patch);
                    } catch (error) {
                        handleCacheUpdateError(error, 'getChat [optimistic]', _arg.conversationId);
                    }

                    // Also update patient chat cache if applicable
                    // Use upsertQueryData to ensure cache entry is created even if it doesn't exist yet
                    if (_arg.patientId) {
                        try {
                            const cacheKey = { conversationId: _arg.conversationId, patientId: _arg.patientId };

                            // Get current cache state to preserve existing messages (like welcome message)
                            // Use RTK Query's select utility for proper cache access
                            const selectResult = chatApi.endpoints.getPatientChat.select(cacheKey);
                            const existingData = selectResult(getState() as Parameters<typeof selectResult>[0])?.data;

                            // Merge existing messages with new user message
                            const existingMessages = existingData?.messages ?? [];
                            const mergedMessages = [...existingMessages, optimisticUserMessage];

                            // upsertQueryData will create or replace the cache entry
                            // Note: upsertQueryData doesn't return an undoable patch, so we don't add it to patches
                            dispatch(
                                chatApi.util.upsertQueryData('getPatientChat', cacheKey, {
                                    messages: mergedMessages,
                                    conversation_title: existingData?.conversation_title ?? '',
                                    conversation_full_path: existingData?.conversation_full_path ?? '',
                                    is_patient_query: true,
                                    pinned: existingData?.pinned ?? false,
                                } as ChatHistoryResponse)
                            );
                        } catch (error) {
                            handleCacheUpdateError(error, 'getPatientChat [optimistic]', _arg.conversationId);
                        }
                    }
                }

                try {
                    const { data: response } = await queryFulfilled;

                    // Update optimistic message timestamp and conversation title (for existing/pre-generated conversations)
                    if (_arg.conversationId && response.timestamp) {
                        const updatedUserMessage: ChatMessageRow = {
                            ...optimisticUserMessage,
                            Timestamp: response.timestamp,
                            ConversationId: response.conversation_id,
                        };

                        try {
                            dispatch(
                                chatApi.util.updateQueryData(
                                    'getChat',
                                    { conversationId: _arg.conversationId },
                                    draft => {
                                        if (!draft?.messages) return;
                                        // Remove optimistic message
                                        const idx = draft.messages.findIndex(
                                            m => m.Timestamp === tempTimestamp && m.content?.role === 'user'
                                        );
                                        if (idx !== -1) {
                                            // Replace with server-timestamped version
                                            draft.messages[idx] = updatedUserMessage;
                                        }
                                        // Update conversation title if available
                                        if (response.conversation_title) {
                                            draft.conversation_title = response.conversation_title;
                                        }
                                    }
                                )
                            );
                        } catch (error) {
                            handleCacheUpdateError(error, 'getChat [update timestamp]', _arg.conversationId);
                        }

                        if (_arg.patientId) {
                            try {
                                dispatch(
                                    chatApi.util.updateQueryData(
                                        'getPatientChat',
                                        { conversationId: _arg.conversationId, patientId: _arg.patientId },
                                        draft => {
                                            if (!draft?.messages) return;
                                            const idx = draft.messages.findIndex(
                                                m => m.Timestamp === tempTimestamp && m.content?.role === 'user'
                                            );
                                            if (idx !== -1) {
                                                draft.messages[idx] = updatedUserMessage;
                                            }
                                            // Update conversation title if available
                                            if (response.conversation_title) {
                                                draft.conversation_title = response.conversation_title;
                                            }
                                        }
                                    )
                                );
                            } catch (error) {
                                handleCacheUpdateError(error, 'getPatientChat [update timestamp]', _arg.conversationId);
                            }

                            // For pre-generated conversation IDs (patient chats), add to Recent Chats history
                            // This ensures the conversation appears in the sidebar immediately
                            const newConversation = {
                                id: response.conversation_id,
                                conversation_id: response.conversation_id,
                                name: response.conversation_title,
                                title: response.conversation_title,
                                displayName: response.conversation_title,
                                is_patient_query: true,
                                patient_id: response.patient_id,
                                timestamp: response.timestamp,
                                pinned: false,
                            };

                            try {
                                dispatch(
                                    chatApi.util.updateQueryData(
                                        'getRecentConversations',
                                        { page: 1, size: 20 },
                                        draft => {
                                            if (!draft?.conversations || !Array.isArray(draft.conversations)) return;
                                            // Check if conversation already exists
                                            const exists = draft.conversations.some(
                                                c =>
                                                    c.id === response.conversation_id ||
                                                    c.conversation_id === response.conversation_id
                                            );
                                            if (!exists) {
                                                draft.conversations.unshift(newConversation);
                                                draft.total = (draft.total || 0) + 1;
                                            }
                                        }
                                    )
                                );
                            } catch (error) {
                                handleCacheUpdateError(
                                    error,
                                    'getRecentConversations [pre-generated]',
                                    response.conversation_id
                                );
                            }
                        }
                    }

                    // Add to cache for new conversations (when no conversationId was passed)
                    if (!_arg.conversationId && response.conversation_id) {
                        const newConversation = {
                            id: response.conversation_id,
                            conversation_id: response.conversation_id,
                            name: response.conversation_title,
                            title: response.conversation_title,
                            displayName: response.conversation_title,
                            is_patient_query: !!response.patient_id,
                            patient_id: response.patient_id,
                            timestamp: response.timestamp,
                            pinned: false,
                        };

                        // Update getRecentConversations cache
                        dispatch(
                            chatApi.util.updateQueryData('getRecentConversations', { page: 1, size: 20 }, draft => {
                                if (!draft?.conversations || !Array.isArray(draft.conversations)) return;
                                // Add to the beginning of the list
                                draft.conversations.unshift(newConversation);
                                draft.total = (draft.total || 0) + 1;
                            })
                        );

                        // Add user message to the new conversation cache so it shows immediately after navigation
                        const newConversationUserMessage: ChatMessageRow = {
                            BatchId: 0,
                            ConversationId: response.conversation_id,
                            Timestamp: response.timestamp,
                            UserId: 'local',
                            content: {
                                role: 'user',
                                content: _arg.message,
                            },
                            ...(typeof response.patient_id === 'string' ? { patientId: response.patient_id } : {}),
                        };

                        try {
                            dispatch(
                                chatApi.util.updateQueryData(
                                    'getChat',
                                    { conversationId: response.conversation_id },
                                    draft => {
                                        if (!draft) {
                                            // Initialize the conversation cache if it doesn't exist
                                            return {
                                                messages: [newConversationUserMessage],
                                                conversation_title: response.conversation_title,
                                                conversation_full_path: '',
                                                is_patient_query: !!response.patient_id,
                                                pinned: false,
                                            } as ChatHistoryResponse;
                                        }
                                        draft.messages.push(newConversationUserMessage);
                                        return draft;
                                    }
                                )
                            );
                        } catch (error) {
                            handleCacheUpdateError(error, 'getChat [new conversation]', response.conversation_id);
                        }

                        if (response.patient_id) {
                            try {
                                dispatch(
                                    chatApi.util.updateQueryData(
                                        'getPatientChat',
                                        { conversationId: response.conversation_id, patientId: response.patient_id },
                                        draft => {
                                            if (!draft) {
                                                return {
                                                    messages: [newConversationUserMessage],
                                                    conversation_title: response.conversation_title,
                                                    conversation_full_path: '',
                                                    is_patient_query: true,
                                                    pinned: false,
                                                } as ChatHistoryResponse;
                                            }
                                            draft.messages.push(newConversationUserMessage);
                                            return draft;
                                        }
                                    )
                                );
                            } catch (error) {
                                handleCacheUpdateError(
                                    error,
                                    'getPatientChat [new conversation]',
                                    response.conversation_id
                                );
                            }
                        }
                    }
                } catch {
                    // Revert optimistic updates on error
                    patches.forEach(patch => patch.undo());
                }
            },
        }),

        streamConversation: builder.query<
            void,
            { conversationId: string; correlationId: string; patientId?: string | null }
        >({
            queryFn: () => ({ data: undefined }),

            async onCacheEntryAdded(
                { conversationId, correlationId, patientId },
                { cacheDataLoaded, cacheEntryRemoved, dispatch }
            ) {
                await cacheDataLoaded;
                const ctrl = new AbortController();
                const streamTimeoutMs = Number(process.env.NEXT_PUBLIC_STREAM_TIMEOUT) || 300000;
                let streamTimeoutId: ReturnType<typeof setTimeout> | null = null;

                const url = `/api/conversation/progress/${conversationId}/${encodeURIComponent(correlationId)}`;

                // Track if we've received final message to stop processing
                let receivedFinal = false;
                // Track if stream was aborted by user
                let isAborted = false;
                // Track if we've already added the user message to cache
                let userMessageCached = false;
                // Track last pre-fetched SQL query to avoid duplicate pre-fetches
                let lastPrefetchedSql: string | null = null;

                // Error context for structured error handling
                const errorContext = {
                    operation: 'streamConversation',
                    conversationId,
                    correlationId,
                    patientId,
                    timestamp: Date.now(),
                    url,
                };

                // Dispatch stream start event
                dispatchStreamEvent({
                    eventName: STREAM_EVENT_NAMES.START,
                    conversationId,
                    onError: error => {
                        const streamError = streamErrorHandler.create('event_dispatch_failed', error, errorContext);
                        streamErrorHandler.log(streamError, 'warn');
                    },
                });

                const abortStream = (reason: string) => {
                    isAborted = true;
                    const cancelError = streamErrorHandler.create('cancelled_by_user', new Error(reason), errorContext);
                    streamErrorHandler.log(cancelError, 'warn');
                    ctrl.abort();
                };

                // Listen for UI stop/cancel to abort the SSE
                const handleStop = (event: Event) => {
                    if (isChatStreamEvent(event) && event.detail.conversationId === conversationId) {
                        abortStream('User stopped stream');
                    }
                };
                window.addEventListener(STREAM_EVENT_NAMES.CANCELLED, handleStop as EventListener);

                streamTimeoutId = setTimeout(() => {
                    const timeoutError = streamErrorHandler.create(
                        'timeout',
                        new Error(`Stream timeout after ${streamTimeoutMs}ms`),
                        errorContext
                    );
                    streamErrorHandler.log(timeoutError, 'warn');
                    // streamErrorHandler.show(timeoutError);
                    // dispatchStreamEvent({
                    //     eventName: STREAM_EVENT_NAMES.TIMEOUT,
                    //     conversationId,
                    //     onError: err => {
                    //         const dispatchError = streamErrorHandler.create('event_dispatch_failed', err, errorContext);
                    //         streamErrorHandler.log(dispatchError, 'warn');
                    //     },
                    // });
                    ctrl.abort();
                }, streamTimeoutMs);

                const headers: Record<string, string> = { 'x-gateway': 'fastapi' };

                const upsertPacket = (pkt: StreamPacket) => {
                    // Skip processing if aborted
                    if (isAborted) return;
                    // Skip processing if we already received final, UNLESS this packet has follow-up questions
                    // (which might arrive in a separate packet after the text completion)
                    const hasFollowUpQuestions =
                        pkt?.response?.assistant_message?.follow_up_questions &&
                        Array.isArray(pkt.response.assistant_message.follow_up_questions) &&
                        pkt.response.assistant_message.follow_up_questions.length > 0;

                    if (receivedFinal && !hasFollowUpQuestions) return;

                    // Validate packet structure
                    const validation = streamErrorHandler.validate(pkt);
                    if (!validation.isValid) {
                        const validationError = streamErrorHandler.create(
                            'parse_error',
                            new Error(`Invalid packet structure: ${validation.errors.join(', ')}`),
                            errorContext
                        );
                        streamErrorHandler.log(validationError);
                        throw new Error(validationError.message);
                    }

                    if (!pkt?.response?.assistant_message) return;

                    const { response, is_final: topLevelIsFinal } = pkt;
                    const assistant = response.assistant_message;

                    // Use timestamp from assistant_message or generate one
                    const timestamp = String(assistant.timestamp ?? new Date().toISOString());
                    const pidFromPayload =
                        response.patient_id && response.patient_id !== 'null' ? response.patient_id : undefined;
                    const pidFromParam = typeof patientId === 'string' ? patientId : undefined;

                    // Cache user message from first chunk (only once)
                    if (!userMessageCached && response.user_message) {
                        userMessageCached = true;
                        const userRow: ChatMessageRow = {
                            BatchId: 0,
                            _id: `user-${conversationId}-${timestamp}`,
                            ConversationId: response.conversation_id || conversationId,
                            Timestamp: timestamp,
                            UserId: 'admin',
                            content: {
                                role: 'user',
                                content: response.user_message,
                            },
                            ...(typeof pidFromPayload === 'string' ? { patientId: pidFromPayload } : {}),
                        };

                        const addUserMessage = (draft?: ChatHistoryResponse) => {
                            // Initialize messages array if it doesn't exist
                            if (!draft) return;
                            if (!draft.messages) {
                                draft.messages = [];
                            }
                            // Check if this specific user message already exists (by content match)
                            // This handles resumption where previous messages exist but current one doesn't
                            const messageExists = draft.messages.some(
                                m => m.content?.role === 'user' && m.content?.content === response.user_message
                            );
                            if (!messageExists) {
                                draft.messages.push(userRow);
                            }
                        };

                        try {
                            dispatch(chatApi.util.updateQueryData('getChat', { conversationId }, addUserMessage));
                        } catch (error) {
                            handleCacheUpdateError(error, 'getChat [user]', conversationId);
                        }

                        const pids = [pidFromPayload, pidFromParam].filter((x): x is string => typeof x === 'string');
                        for (const pid of pids) {
                            try {
                                dispatch(
                                    chatApi.util.updateQueryData(
                                        'getPatientChat',
                                        { conversationId, patientId: pid },
                                        addUserMessage
                                    )
                                );
                            } catch (error) {
                                handleCacheUpdateError(error, 'getPatientChat [user]', conversationId);
                            }
                        }
                    }

                    // Check if final from multiple sources
                    // Also treat filter_confirmation with confirmation_required as a stopping point
                    const isFilterConfirmation =
                        assistant.stage === 'filter_confirmation' && assistant.confirmation_required === true;
                    const isFinal =
                        !!topLevelIsFinal ||
                        assistant.stage === 'final_response' ||
                        !!assistant.is_final ||
                        isFilterConfirmation ||
                        assistant.traffic === 'stop';

                    // Pre-fetch SQL query results when SQL is detected (even before final response)
                    // This optimizes table loading by starting execution early
                    const sqlQuery =
                        typeof assistant.sql_query === 'string' && assistant.sql_query.trim()
                            ? assistant.sql_query.trim()
                            : Array.isArray(assistant.sql_queries) && assistant.sql_queries[0]?.query
                              ? assistant.sql_queries[0].query.trim()
                              : null;

                    if (sqlQuery && sqlQuery !== lastPrefetchedSql && conversationId) {
                        // Update last pre-fetched SQL to avoid duplicate fetches
                        lastPrefetchedSql = sqlQuery;

                        // Determine patient ID for query args
                        const effectivePatientId = pidFromPayload || pidFromParam;

                        // Pre-fetch table data in background
                        // Use default pagination (page 1, page_size 20) for initial load
                        try {
                            dispatch(
                                chatApi.util.prefetch(
                                    'getTableData',
                                    {
                                        raw_query: sqlQuery,
                                        person_id: effectivePatientId ? String(effectivePatientId) : undefined,
                                        page: 1,
                                        page_size: 20,
                                        type: 'dynamic' as const,
                                        document_id: undefined,
                                        search: undefined,
                                        sort_by: undefined,
                                    },
                                    { force: false } // Don't force if already cached
                                )
                            );
                        } catch (prefetchError) {
                            // Silently fail pre-fetch - normal execution will handle it
                            logger.debug('[Stream] Pre-fetch failed (non-critical)', prefetchError);
                        }
                    }

                    if (isFinal) {
                        // Only perform finalization logic if this is the first time we see final
                        if (!receivedFinal) {
                            // Mark as received to skip any subsequent chunks
                            receivedFinal = true;
                            if (streamTimeoutId) {
                                clearTimeout(streamTimeoutId);
                                streamTimeoutId = null;
                            }
                            // Clear all session data for this conversation
                            sessionStore.clearConversation(conversationId);
                            // Reset pre-fetch tracking for next conversation
                            lastPrefetchedSql = null;
                            // Dispatch stream final event
                            dispatchStreamEvent({
                                eventName: STREAM_EVENT_NAMES.FINAL,
                                conversationId,
                                onError: error => {
                                    const dispatchError = streamErrorHandler.create(
                                        'event_dispatch_failed',
                                        error,
                                        errorContext
                                    );
                                    streamErrorHandler.log(dispatchError, 'warn');
                                },
                            });
                        }
                    }

                    // Build content for display
                    // For final messages with no text, show "No results found"
                    const finalText = isFinal
                        ? assistant.text?.trim()
                            ? assistant.text
                            : 'No results found for your query.'
                        : '';

                    // If backend indicates conversation removal, remove from list immediately
                    if (finalText === 'No results found. Conversation removed.') {
                        try {
                            dispatch(
                                chatApi.util.updateQueryData('getRecentConversations', { page: 1, size: 20 }, draft => {
                                    if (!draft?.conversations) return;
                                    const initialLength = draft.conversations.length;
                                    draft.conversations = draft.conversations.filter(
                                        c => c.id !== conversationId && c.conversation_id !== conversationId
                                    );
                                    if (draft.conversations.length < initialLength) {
                                        draft.total = Math.max(0, (draft.total || 0) - 1);
                                    }
                                })
                            );
                        } catch (error) {
                            handleCacheUpdateError(error, 'getRecentConversations [removal]', conversationId);
                        }
                    }

                    const content = {
                        role: 'assistant' as const,
                        content: {
                            ...assistant,
                            text: finalText,
                            is_final: isFinal,
                            // For non-final messages, use temp_message from backend (not text!)
                            temp_message: !isFinal ? (assistant.temp_message ?? '') : '',
                            thought_process: assistant.thought_process,
                        },
                        is_final: isFinal,
                        temp_message: !isFinal ? (assistant.temp_message ?? '') : '',
                        stage: assistant.stage,
                    };
                    // For non-final (thinking) messages, generate unique ID to accumulate all steps
                    // For final messages, use the backend _id for proper deduplication
                    const messageId = isFinal
                        ? response?._id
                        : `thinking-${conversationId}-${timestamp}-${assistant.stage || 'unknown'}-${assistant.temp_message?.slice(0, 20) || ''}`;

                    const row: ChatMessageRow = {
                        BatchId: 11,
                        _id: messageId,
                        ConversationId: response.conversation_id || conversationId,
                        Timestamp: timestamp,
                        UserId: 'admin',
                        content,
                        correlationId,
                        ...(typeof pidFromPayload === 'string' ? { patientId: pidFromPayload } : {}),
                    };

                    const patchFn = (draft?: ChatHistoryResponse) => {
                        if (!draft?.messages) return;

                        // For thinking messages, check by unique ID to accumulate all steps
                        // For final messages, find by _id + role for deduplication
                        const idx = draft.messages.findIndex(m => m._id === row._id && m.content?.role === 'assistant');
                        if (idx === -1) {
                            draft.messages.push(row);
                        } else {
                            // Replace existing message with updated one
                            draft.messages[idx] = row;
                        }

                        if (
                            assistant.summary_substring &&
                            typeof assistant.summary_substring === 'string' &&
                            assistant.summary_substring.trim().length > 0
                        ) {
                            draft.conversation_title = assistant.summary_substring.trim();
                        }
                    };

                    try {
                        dispatch(chatApi.util.updateQueryData('getChat', { conversationId }, patchFn));
                    } catch (error) {
                        handleCacheUpdateError(error, 'getChat', conversationId);
                    }

                    const candidates = [pidFromPayload, pidFromParam].filter((x): x is string => typeof x === 'string');
                    for (const pidCandidate of candidates) {
                        try {
                            dispatch(
                                chatApi.util.updateQueryData(
                                    'getPatientChat',
                                    { conversationId, patientId: pidCandidate },
                                    patchFn
                                )
                            );
                        } catch (error) {
                            handleCacheUpdateError(error, 'getPatientChat', conversationId);
                        }
                    }

                    if (
                        assistant.summary_substring &&
                        typeof assistant.summary_substring === 'string' &&
                        assistant.summary_substring.trim().length > 0
                    ) {
                        try {
                            const newTitle = assistant.summary_substring.trim();

                            // Helper to update a conversation in a list
                            const updateConversationInList = (conversations: any[] | undefined) => {
                                if (!conversations) return false;
                                const conversation = conversations.find(
                                    c => c.id === conversationId || c.conversation_id === conversationId
                                );
                                if (conversation) {
                                    conversation.title = newTitle;
                                    conversation.displayName = newTitle;
                                    conversation.name = newTitle;
                                    return true;
                                }
                                return false;
                            };

                            // Update pinned conversations
                            try {
                                dispatch(
                                    chatApi.util.updateQueryData('getPinnedConversations', undefined, draft => {
                                        updateConversationInList(draft?.conversations);
                                    })
                                );
                            } catch (error) {
                                handleCacheUpdateError(error, 'getPinnedConversations [title update]', conversationId);
                            }

                            // Update recent conversations (page 1 - most common)
                            try {
                                dispatch(
                                    chatApi.util.updateQueryData(
                                        'getRecentConversations',
                                        { page: 1, size: 20 },
                                        draft => {
                                            updateConversationInList(draft?.conversations);
                                        }
                                    )
                                );
                            } catch (error) {
                                handleCacheUpdateError(error, 'getRecentConversations [title update]', conversationId);
                            }

                            // Update getUserConversationHistory for common query args
                            // We update the most common cases (owned, page 1)
                            const commonQueryArgs: Array<{
                                conversation_type: 'owned' | 'shared';
                                page: number;
                                size: number;
                            }> = [
                                { conversation_type: 'owned', page: 1, size: 100 },
                                { conversation_type: 'shared', page: 1, size: 100 },
                            ];

                            for (const queryArgs of commonQueryArgs) {
                                try {
                                    dispatch(
                                        chatApi.util.updateQueryData('getUserConversationHistory', queryArgs, draft => {
                                            const conversations = (draft as ConversationListResponse)?.conversations;
                                            updateConversationInList(conversations);
                                        })
                                    );
                                } catch {
                                    // Silently fail for cache entries that don't exist
                                }
                            }
                            const tabIds: string[] = [conversationId]
                                .concat(
                                    typeof pidFromPayload === 'string' ? [`${conversationId}:${pidFromPayload}`] : []
                                )
                                .concat(typeof pidFromParam === 'string' ? [`${conversationId}:${pidFromParam}`] : []);
                            for (const tabId of tabIds) {
                                try {
                                    dispatch(updateTabLabel({ tabId, label: newTitle }));
                                } catch {
                                    // Silently fail if tab update fails
                                }
                            }
                        } catch (error) {
                            handleCacheUpdateError(error, 'updateConversationTitle [lists]', conversationId);
                        }
                    }
                };

                try {
                    await fetchEventSource(url, {
                        signal: ctrl.signal,
                        credentials: 'include',
                        headers,
                        onopen: (res: SSEOpenResponse) => {
                            if (!res.ok) {
                                const httpError = streamErrorHandler.createHTTP(
                                    res.status,
                                    res.statusText,
                                    errorContext
                                );
                                streamErrorHandler.log(httpError);
                                streamErrorHandler.show(httpError);
                                throw new Error(httpError.message);
                            }
                            return Promise.resolve();
                        },
                        onmessage: (evt: SSEMessageEvent) => {
                            const parsed = parseSSEMessage(evt);
                            if (!parsed) {
                                const trimmed = (evt.data ?? '').trim();
                                if (!trimmed || trimmed === '[DONE]' || evt.event === 'final') {
                                    return;
                                }
                                const parseError = streamErrorHandler.create(
                                    'parse_error',
                                    new Error('Failed to parse SSE message'),
                                    errorContext
                                );
                                streamErrorHandler.log(parseError, 'warn');
                                return; // Skip invalid messages without aborting
                            }

                            // Detect backend-emitted timeout or inactivity timeout
                            const stage = parsed.packet?.response?.assistant_message?.stage;
                            if (isBackendTimeoutStage(stage)) {
                                const timeoutError = streamErrorHandler.create(
                                    'timeout',
                                    new Error(`Stream ${stage}`),
                                    errorContext
                                );
                                streamErrorHandler.log(timeoutError, 'warn');
                                streamErrorHandler.show(timeoutError);

                                dispatchStreamEvent({
                                    eventName: STREAM_EVENT_NAMES.TIMEOUT,
                                    conversationId,
                                    onError: err => {
                                        const dispatchError = streamErrorHandler.create(
                                            'event_dispatch_failed',
                                            err,
                                            errorContext
                                        );
                                        streamErrorHandler.log(dispatchError, 'warn');
                                    },
                                });
                                if (streamTimeoutId) {
                                    clearTimeout(streamTimeoutId);
                                    streamTimeoutId = null;
                                }
                                ctrl.abort();
                                return;
                            }

                            try {
                                upsertPacket(parsed.packet);
                            } catch (packetError) {
                                // Validation or processing error
                                const processingError = streamErrorHandler.create(
                                    'parse_error',
                                    packetError,
                                    errorContext
                                );
                                streamErrorHandler.log(processingError);
                                streamErrorHandler.show(processingError);

                                dispatchStreamEvent({
                                    eventName: STREAM_EVENT_NAMES.ERROR,
                                    conversationId,
                                    onError: error => {
                                        const dispatchError = streamErrorHandler.create(
                                            'event_dispatch_failed',
                                            error,
                                            errorContext
                                        );
                                        streamErrorHandler.log(dispatchError, 'warn');
                                    },
                                });
                                ctrl.abort();
                            }
                        },
                        onerror: (err: unknown) => {
                            // Connection or stream error
                            const connectionError = streamErrorHandler.create('connection_failed', err, errorContext);
                            streamErrorHandler.log(connectionError);
                            streamErrorHandler.show(connectionError);

                            dispatchStreamEvent({
                                eventName: STREAM_EVENT_NAMES.ERROR,
                                conversationId,
                                onError: error => {
                                    const dispatchError = streamErrorHandler.create(
                                        'event_dispatch_failed',
                                        error,
                                        errorContext
                                    );
                                    streamErrorHandler.log(dispatchError, 'warn');
                                },
                            });
                            if (streamTimeoutId) {
                                clearTimeout(streamTimeoutId);
                                streamTimeoutId = null;
                            }
                            ctrl.abort();
                        },
                    });
                } catch (e) {
                    // Handle AbortError silently (user cancelled)
                    if ((e as Error).name === 'AbortError') {
                        const abortError = streamErrorHandler.create('abort_error', e, errorContext);
                        streamErrorHandler.log(abortError, 'warn');
                        // Inform UI to stop thinking
                        dispatchStreamEvent({
                            eventName: STREAM_EVENT_NAMES.CLOSED,
                            conversationId,
                            onError: error => {
                                const dispatchError = streamErrorHandler.create(
                                    'event_dispatch_failed',
                                    error,
                                    errorContext
                                );
                                streamErrorHandler.log(dispatchError, 'warn');
                            },
                        });
                    } else {
                        // Unexpected error in stream setup
                        const unexpectedError = streamErrorHandler.create('unknown_error', e, errorContext);
                        streamErrorHandler.log(unexpectedError);
                        streamErrorHandler.show(unexpectedError);
                    }
                }

                await cacheEntryRemoved;
                // Dispatch stream closed event
                dispatchStreamEvent({
                    eventName: STREAM_EVENT_NAMES.CLOSED,
                    conversationId,
                    onError: error => {
                        const dispatchError = streamErrorHandler.create('event_dispatch_failed', error, errorContext);
                        streamErrorHandler.log(dispatchError, 'warn');
                    },
                });
                // Cleanup listeners for stop/cancel events
                window.removeEventListener(STREAM_EVENT_NAMES.CANCELLED, handleStop as EventListener);
                if (streamTimeoutId) {
                    clearTimeout(streamTimeoutId);
                    streamTimeoutId = null;
                }
                ctrl.abort();
            },
        }),

        queryTable: builder.mutation<QueryTableResponse, QueryTableArgs>({
            query: ({ raw_query, person_id, page = 1, page_size = 20, start_date, end_date, document_id }) => {
                const payload = {
                    raw_query,
                    patient_id: person_id,
                    request_type: 'table',
                    page,
                    start_date,
                    end_date,
                    page_size,
                    ...((document_id && document_id !== '1' && { document_id }) || {}),
                };
                return {
                    url: `rdb-query/query`,
                    method: 'POST',
                    body: payload,
                };
            },
        }),

        /** Get OMOP database schema for SQL editor autocomplete */
        getOmopSchema: builder.query<
            Array<{
                name: string;
                schema: string;
                rowCount: number;
                columnCount: number;
                sizeBytes: number;
                columns: Array<{
                    name: string;
                    type: string;
                    nullable: boolean;
                }>;
            }>,
            void
        >({
            query: () => ({
                url: '/db-explorer/jsl-omop-default/schema',
                method: 'GET',
            }),
            // Cache schema for 1 hour - schema rarely changes
            keepUnusedDataFor: 3600,
        }),

        getTableData: builder.query<QueryTableResponse, QueryTableArgs>({
            query: ({
                raw_query,
                person_id,
                page = 1,
                page_size = 20,
                start_date,
                end_date,
                document_id,
                search,
                sort_by,
                sort_order,
            }) => ({
                url: 'rdb-query/query',
                method: 'POST',
                body: {
                    raw_query,
                    patient_id: person_id,
                    request_type: 'table',
                    page,
                    page_size,
                    start_date,
                    end_date,
                    ...(document_id && document_id !== '1' ? { document_id } : {}),
                    ...(search ? { search } : {}),
                    ...(sort_by ? { sort_by } : {}),
                    ...(sort_order ? { sort_order } : {}),
                },
            }),
            transformResponse: (res: QueryTableResponse): QueryTableResponse => ({
                records: res?.records ?? [],
                total_records: res?.total_records ?? 0,
                has_next: res?.has_next ?? false,
                page: typeof res?.page === 'number' ? res.page : 1,
                page_size: typeof res?.page_size === 'number' ? res.page_size : 20,
            }),
            providesTags: (_res, _err, args) => [{ type: 'Documents' as const, id: args.document_id ?? 'dynamic' }],
            // Cache table data for 10 minutes
            keepUnusedDataFor: 600,
        }),

        downloadTableDataCsv: builder.mutation<Blob, { raw_query: string; person_ids?: number[] }>({
            query: ({ raw_query, person_ids }) => ({
                url: 'rdb-query/download',
                method: 'POST',
                body: {
                    raw_query,
                    request_type: 'table',
                    ...(person_ids && person_ids.length > 0 ? { person_ids } : {}),
                },
                responseHandler: response => response.blob(),
            }),
        }),

        getCorrelationStatus: builder.query<CorrelationStatusResponse, { correlationId: string }>({
            query: ({ correlationId }) => ({
                url: `conversation/correlation/${encodeURIComponent(correlationId)}`,
                method: 'GET',
            }),
        }),

        stopConversation: builder.mutation<
            { message: string },
            { conversationId: string; correlationId: string; patientId?: string | null }
        >({
            query: ({ correlationId }) => ({
                url: `conversation/stream/${encodeURIComponent(correlationId)}`,
                method: 'DELETE',
            }),
        }),

        getUserConversationHistory: builder.query<ConversationListResponse, ChatHistoryRequestType>({
            query: ({
                page = DEFAULT_PAGE,
                size = DEFAULT_PAGE_SIZE,
                conversation_type,
                start_date,
                end_date,
                search_query,
            }) => {
                const params: Record<string, any> = {
                    conversation_type,
                    page,
                    size,
                };

                if (start_date) params.start_date = start_date;
                if (end_date) params.end_date = end_date;
                if (search_query && search_query.trim()) params.search_query = search_query.trim();

                return {
                    url: '/conversation',
                    method: 'GET',
                    params,
                };
            },

            keepUnusedDataFor: 300,

            providesTags: (_res, _err, arg) => {
                return [
                    {
                        type: 'Conversation',
                        id: `${arg.conversation_type}:${arg.page ?? DEFAULT_PAGE}:${arg.size ?? DEFAULT_PAGE_SIZE}`,
                    },
                    'Messages',
                ];
            },
        }),

        /** Get pinned conversations only */
        getPinnedConversations: builder.query<{ conversations: any[] }, void>({
            query: () => ({
                url: '/conversation/pinned',
                method: 'GET',
            }),
            providesTags: ['PinnedConversations'],
        }),

        /** Get recent (non-pinned) conversations with pagination */
        getRecentConversations: builder.query<
            { conversations: any[]; page: number; size: number; hasMore: boolean; total: number },
            { page?: number; size?: number }
        >({
            query: ({ page = 1, size = 20 }) => ({
                url: '/conversation/recent',
                method: 'GET',
                params: { page, size },
            }),
            serializeQueryArgs: ({ endpointName }) => {
                return endpointName;
            },
            merge: (currentCache, newItems, { arg }) => {
                if (arg.page === 1) {
                    return newItems;
                }
                return {
                    ...newItems,
                    conversations: [...currentCache.conversations, ...newItems.conversations],
                };
            },
            forceRefetch({ currentArg, previousArg }) {
                return currentArg?.page !== previousArg?.page;
            },
            providesTags: ['RecentConversations'],
            transformResponse: (response: {
                conversations: any[];
                page: number;
                size: number;
                hasMore: boolean;
                total: number;
            }) => {
                // Filter out empty conversations or those with "No results found" title if backend hasn't cleaned them up yet
                if (response && Array.isArray(response.conversations)) {
                    response.conversations = response.conversations.filter(c => {
                        // Filter by title if it indicates empty result
                        if (
                            c.title === 'No results found. Conversation removed.' ||
                            c.name === 'No results found. Conversation removed.'
                        ) {
                            return false;
                        }
                        return true;
                    });
                }
                return response;
            },
        }),

        /** Toggle pin status for a conversation */
        togglePin: builder.mutation<{ message: string }, { conversationId: string; pinned: boolean }>({
            query: ({ conversationId, pinned }) => ({
                url: `conversation/${conversationId}/pin`,
                method: 'PUT',
                body: { pinned },
            }),
            invalidatesTags: [
                { type: 'Conversation', id: `owned:${DEFAULT_PAGE}:${DEFAULT_PAGE_SIZE}` },
                'PinnedConversations',
                'RecentConversations',
            ],
        }),

        /** Delete a conversation by id */
        deleteConversationOrFolder: builder.mutation<DeleteConversationResponse, { id: string }>({
            query: ({ id }) => ({
                url: `conversation/${id}`,
                method: 'DELETE',
            }),
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                // Optimistically remove from pinned conversations cache
                const pinnedPatch = dispatch(
                    chatApi.util.updateQueryData('getPinnedConversations', undefined, draft => {
                        if (!draft?.conversations) return;
                        draft.conversations = draft.conversations.filter(c => c.id !== id && c.conversation_id !== id);
                    })
                );

                // Optimistically remove from recent conversations cache
                const recentPatch = dispatch(
                    chatApi.util.updateQueryData('getRecentConversations', { page: 1, size: 20 }, draft => {
                        if (!draft?.conversations) return;
                        const initialLength = draft.conversations.length;
                        draft.conversations = draft.conversations.filter(c => c.id !== id && c.conversation_id !== id);
                        if (draft.conversations.length < initialLength) {
                            draft.total = Math.max(0, (draft.total || 0) - 1);
                        }
                    })
                );

                try {
                    await queryFulfilled;
                } catch {
                    // Revert optimistic updates on error
                    pinnedPatch.undo();
                    recentPatch.undo();
                }
            },
            invalidatesTags: [
                { type: 'Conversation', id: `owned:${DEFAULT_PAGE}:${DEFAULT_PAGE_SIZE}` },
                'PinnedConversations',
                'RecentConversations',
            ],
        }),

        /** Share conversation with users */
        shareConversation: builder.mutation<
            ShareConversationResponse,
            { conversation_id: string; requestBody: ShareConversationRequestBody[] }
        >({
            query: ({ conversation_id, requestBody }) => ({
                url: `conversation/${conversation_id}/share`,
                method: 'POST',
                body: requestBody,
            }),
            invalidatesTags: [{ type: 'Conversation', id: `shared:${DEFAULT_PAGE}:${DEFAULT_PAGE_SIZE}` }],
        }),

        /** Unshare conversation from users */
        unshareConversation: builder.mutation<
            ShareConversationResponse,
            { conversation_id: string; requestBody: ShareConversationRequestBody[] }
        >({
            query: ({ conversation_id, requestBody }) => ({
                url: `conversation/${conversation_id}/unshare`, // NestJS route
                method: 'POST',
                body: requestBody,
            }),
            invalidatesTags: [{ type: 'Conversation', id: `shared:${DEFAULT_PAGE}:${DEFAULT_PAGE_SIZE}` }],
        }),

        /** Fork conversation from a specific assistant message */
        forkConversation: builder.mutation<
            ForkConversationResponse,
            { conversationId: string; messageId: string; patientId?: string }
        >({
            query: ({ conversationId, messageId, patientId }) => ({
                url: 'conversation/fork',
                method: 'POST',
                body: { conversationId, messageId, patientId },
            }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data: forkedConversation } = await queryFulfilled;

                    // Create new conversation item for the list (NodeDto format)
                    const newConversation = {
                        id: forkedConversation.conversation_id,
                        conversation_id: forkedConversation.conversation_id,
                        name: forkedConversation.conversation_title,
                        title: forkedConversation.conversation_title,
                        displayName: forkedConversation.conversation_title,
                        is_patient_query: forkedConversation.is_patient_query,
                        patient_id: forkedConversation.patient_id,
                        timestamp: forkedConversation.timestamp,
                        pinned: forkedConversation.pinned,
                    };

                    // Update getRecentConversations cache
                    try {
                        dispatch(
                            chatApi.util.updateQueryData('getRecentConversations', { page: 1, size: 20 }, draft => {
                                if (!draft?.conversations || !Array.isArray(draft.conversations)) return;
                                draft.conversations.unshift(newConversation);
                                draft.total = (draft.total || 0) + 1;
                            })
                        );
                    } catch (error) {
                        handleCacheUpdateError(
                            error,
                            'getRecentConversations',
                            String(forkedConversation.conversation_id)
                        );
                    }
                } catch {
                    // Query failed - invalidation tags will handle refetch
                }
            },
            invalidatesTags: [
                { type: 'Conversation', id: `owned:${DEFAULT_PAGE}:${DEFAULT_PAGE_SIZE}` },
                { type: 'Conversation', id: `owned:false:${DEFAULT_PAGE}:${DEFAULT_PAGE_SIZE}` },
                { type: 'Conversation', id: `owned:true:${DEFAULT_PAGE}:${DEFAULT_PAGE_SIZE}` },
            ],
        }),

        /** Submit user feedback (thumbs up/down) for a message */
        submitUserFeedback: builder.mutation<
            { success: boolean; message: string; feedback_id?: string },
            {
                message_id: string;
                conversation_id: string;
                feedback_type: 'thumbs_up' | 'thumbs_down';
                natural_language_query?: string;
                sql_query?: string;
                patient_id?: string;
            }
        >({
            query: body => ({
                url: 'conversation/feedback/user',
                method: 'POST',
                body,
            }),
            async onQueryStarted(
                { conversation_id, message_id, feedback_type, patient_id },
                { dispatch, queryFulfilled }
            ) {
                // Optimistically update the cache with the new feedback
                const updateFeedback = (draft?: ChatHistoryResponse) => {
                    if (!draft) return;
                    draft.last_message_feedback = {
                        message_id,
                        feedback_type,
                    };
                };

                const chatPatch = dispatch(
                    chatApi.util.updateQueryData('getChat', { conversationId: conversation_id }, updateFeedback)
                );

                // Also update patient chat cache if applicable
                let patientChatPatch: { undo: () => void } | undefined;
                if (patient_id) {
                    patientChatPatch = dispatch(
                        chatApi.util.updateQueryData(
                            'getPatientChat',
                            { conversationId: conversation_id, patientId: patient_id },
                            updateFeedback
                        )
                    );
                }

                try {
                    await queryFulfilled;
                } catch {
                    // Revert optimistic updates on error
                    chatPatch.undo();
                    patientChatPatch?.undo();
                }
            },
            invalidatesTags: ['Feedback'],
        }),

        /** Reset current user's feedback for a message (delete entry) */
        resetUserFeedback: builder.mutation<
            { success: boolean; message: string; feedback_id?: string },
            { message_id: string; conversation_id?: string; patient_id?: string }
        >({
            query: ({ message_id }) => ({
                url: `conversation/feedback/user/${message_id}`,
                method: 'DELETE',
            }),
            async onQueryStarted({ conversation_id, message_id, patient_id }, { dispatch, queryFulfilled }) {
                const updateFeedback = (draft?: ChatHistoryResponse) => {
                    if (!draft) return;
                    draft.last_message_feedback = {
                        message_id,
                        feedback_type: null,
                    };
                };

                let chatPatch: { undo: () => void } | undefined;
                if (conversation_id) {
                    chatPatch = dispatch(
                        chatApi.util.updateQueryData('getChat', { conversationId: conversation_id }, updateFeedback)
                    );
                }

                let patientChatPatch: { undo: () => void } | undefined;
                if (conversation_id && patient_id) {
                    patientChatPatch = dispatch(
                        chatApi.util.updateQueryData(
                            'getPatientChat',
                            { conversationId: conversation_id, patientId: patient_id },
                            updateFeedback
                        )
                    );
                }

                try {
                    await queryFulfilled;
                } catch {
                    chatPatch?.undo();
                    patientChatPatch?.undo();
                }
            },
            invalidatesTags: ['Feedback'],
        }),

        /** Admin marks a message as feedback (auto-approved, goes to VDB) */
        adminMarkFeedback: builder.mutation<
            { success: boolean; message: string; feedback_id?: string; record_id?: number },
            {
                natural_language_query: string;
                sql_query: string;
                category?: string;
                message_id?: string;
                conversation_id?: string;
            }
        >({
            query: body => ({
                url: 'conversation/feedback/admin',
                method: 'POST',
                body,
            }),
            invalidatesTags: result => (result?.success ? ['Feedback'] : []),
        }),

        /** Send confirmation response for multi-filter flow */
        sendConfirmation: builder.mutation<
            StartResponse,
            {
                conversationId: string;
                confirmed: boolean;
                patientId?: string;
                /** Original correlation_id from filter_confirmation - used for context, NOT for streaming */
                parentCorrelationId?: string;
            }
        >({
            query: ({ conversationId, confirmed, patientId, parentCorrelationId }) => ({
                url: 'conversation',
                method: 'POST',
                body: {
                    user_message: confirmed ? 'yes' : 'start fresh',
                    conversation_id: conversationId,
                    ...(patientId ? { patient_id: patientId } : {}),
                    // Pass parent_correlation_id for context linkage, backend generates NEW correlation_id
                    ...(parentCorrelationId ? { parent_correlation_id: parentCorrelationId } : {}),
                    is_confirmation_response: true, // Flag for backend to identify this as confirmation
                },
            }),
            async onQueryStarted({ conversationId, confirmed, patientId }, { dispatch, queryFulfilled }) {
                // Optimistically add user's choice to the UI
                const tempTimestamp = new Date().toISOString();
                const confirmationMessage: ChatMessageRow = {
                    BatchId: 0,
                    ConversationId: conversationId,
                    Timestamp: tempTimestamp,
                    UserId: 'local',
                    content: {
                        role: 'user',
                        content: confirmed ? 'yes' : 'start fresh',
                    },
                };

                // Update chat cache
                const chatPatch = dispatch(
                    chatApi.util.updateQueryData('getChat', { conversationId }, draft => {
                        if (!draft?.messages) return;
                        draft.messages.push(confirmationMessage);
                    })
                );

                // Update patient chat cache if applicable
                let patientPatch: { undo: () => void } | undefined;
                if (patientId) {
                    patientPatch = dispatch(
                        chatApi.util.updateQueryData('getPatientChat', { conversationId, patientId }, draft => {
                            if (!draft?.messages) return;
                            draft.messages.push(confirmationMessage);
                        })
                    );
                }

                try {
                    const { data: response } = await queryFulfilled;

                    // Update with server timestamp
                    dispatch(
                        chatApi.util.updateQueryData('getChat', { conversationId }, draft => {
                            if (!draft?.messages) return;
                            const idx = draft.messages.findIndex(
                                m => m.Timestamp === tempTimestamp && m.content?.role === 'user'
                            );
                            if (idx !== -1) {
                                draft.messages[idx] = {
                                    ...draft.messages[idx],
                                    Timestamp: response.timestamp,
                                };
                            }
                        })
                    );

                    if (patientId) {
                        dispatch(
                            chatApi.util.updateQueryData('getPatientChat', { conversationId, patientId }, draft => {
                                if (!draft?.messages) return;
                                const idx = draft.messages.findIndex(
                                    m => m.Timestamp === tempTimestamp && m.content?.role === 'user'
                                );
                                if (idx !== -1) {
                                    draft.messages[idx] = {
                                        ...draft.messages[idx],
                                        Timestamp: response.timestamp,
                                    };
                                }
                            })
                        );
                    }
                } catch {
                    // Revert on error
                    chatPatch.undo();
                    patientPatch?.undo();
                }
            },
        }),

        /** Get all feedback for admin review with pagination */
        getAllFeedback: builder.query<
            {
                feedbacks: Array<{
                    id: string;
                    message_id: string;
                    conversation_id: string;
                    conversation_is_active?: boolean;
                    user_id: string;
                    user_name?: string;
                    feedback_type: 'thumbs_up' | 'thumbs_down';
                    natural_language_query?: string;
                    sql_query?: string;
                    is_approved: boolean;
                    approved_at?: string;
                    record_id?: number;
                    created_at: string;
                    updated_at: string;
                }>;
                total: number;
                page: number;
                size: number;
                hasMore: boolean;
            },
            { page?: number; size?: number; feedback_type?: string; is_approved?: boolean; search?: string }
        >({
            query: ({ page = 1, size = 20, feedback_type, is_approved, search }) => ({
                url: '/conversation/feedback',
                method: 'GET',
                params: { page, size, feedback_type, is_approved, search },
            }),
            providesTags: ['Feedback'],
        }),

        /** Admin approves user feedback and sends to VDB */
        approveFeedback: builder.mutation<
            { success: boolean; message: string; feedback_id?: string; record_id?: number },
            { feedback_id: string; category?: string }
        >({
            query: body => ({
                url: 'conversation/feedback/approve',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Feedback'],
        }),

        /** Delete feedback entry */
        deleteFeedback: builder.mutation<
            { success: boolean; message: string; feedback_id?: string },
            { feedback_id: string }
        >({
            query: ({ feedback_id }) => ({
                url: `conversation/feedback/${feedback_id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Feedback'],
        }),

        /** Get feedback status for a specific message */
        getFeedbackForMessage: builder.query<
            { feedback_type: 'thumbs_up' | 'thumbs_down' | null },
            { message_id: string }
        >({
            query: ({ message_id }) => ({
                url: `conversation/feedback/message/${message_id}`,
                method: 'GET',
            }),
        }),

        /** Download conversation as CSV (owner or admin) */
        downloadConversation: builder.mutation<Blob, { conversation_id: string }>({
            query: body => ({
                url: 'conversation/download',
                method: 'POST',
                body,
                responseHandler: async response => response.blob(),
            }),
        }),
    }),
});

export const {
    useGetChatQuery,
    useGetPatientChatQuery,
    useGetConversationSamplesQuery,
    useExplainConversationMutation,
    useGetQueryStatsMutation,
    useSendMessageMutation,
    useLazyStreamConversationQuery,
    useQueryTableMutation,
    useGetTableDataQuery,
    useDownloadTableDataCsvMutation,
    useTogglePinMutation,
    useLazyGetTableDataQuery,
    useLazyGetCorrelationStatusQuery,
    useStopConversationMutation,
    useGetUserConversationHistoryQuery,
    useGetPinnedConversationsQuery,
    useGetRecentConversationsQuery,
    useForkConversationMutation,
    useDeleteConversationOrFolderMutation,
    useSendConfirmationMutation,
    // Feedback hooks
    useSubmitUserFeedbackMutation,
    useAdminMarkFeedbackMutation,
    useGetAllFeedbackQuery,
    useApproveFeedbackMutation,
    useResetUserFeedbackMutation,
    useDeleteFeedbackMutation,
    useGetFeedbackForMessageQuery,
    useDownloadConversationMutation,
    // Schema hook
    useGetOmopSchemaQuery,
    useGetConversationByMessageIdQuery,
} = chatApi;
